import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, ArrowLeft } from "lucide-react";

const BookingPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [vehiclesRes, timeSlotsRes] = await Promise.all([
        axios.get(`${API}/vehicles`),
        axios.get(`${API}/timeslots`),
      ]);
      setVehicles(vehiclesRes.data);
      setTimeSlots(timeSlotsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();

    if (!selectedVehicle || !selectedDate || !selectedTimeSlot) {
      toast.error("Please fill all fields");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/parking/book`, {
        vehicle_id: selectedVehicle,
        date: selectedDate,
        time_slot_id: selectedTimeSlot,
      });
      toast.success("Parking booked successfully!");
      navigate("/dashboard");
    } catch (error) {
      if (error.response?.data?.detail?.includes("carpool")) {
        toast.error(error.response.data.detail, {
          action: {
            label: "Find Carpool",
            onClick: () => navigate("/carpool"),
          },
        });
      } else {
        toast.error(error.response?.data?.detail || "Booking failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="booking-page">
      <div className="pt-12 pb-12 px-6 max-w-3xl mx-auto">
        <Button
          data-testid="back-to-dashboard"
          onClick={() => navigate("/dashboard")}
          variant="ghost"
          className="mb-6 text-[#1A3C34] hover:bg-[#F5F2EB] rounded-full"
        >
          <ArrowLeft className="mr-2" size={20} />
          Back to Dashboard
        </Button>

        <div className="fade-in">
          <div className="mb-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-4">
              Book Parking Slot
            </h1>
            <p className="text-xl text-gray-600">Reserve your parking spot in advance.</p>
          </div>

          <Card className="p-8 border border-gray-100">
            {vehicles.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="mx-auto mb-4 text-gray-400" size={48} />
                <p className="text-gray-600 text-lg mb-4">You need to add a vehicle first</p>
                <Button
                  data-testid="add-vehicle-first"
                  onClick={() => navigate("/vehicles")}
                  className="bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full px-6"
                >
                  Add Vehicle
                </Button>
              </div>
            ) : (
              <form onSubmit={handleBooking} className="space-y-6">
                <div>
                  <Label htmlFor="vehicle" className="text-gray-700 font-medium text-lg mb-3 block">
                    Select Vehicle
                  </Label>
                  <Select value={selectedVehicle} onValueChange={setSelectedVehicle}>
                    <SelectTrigger
                      data-testid="select-vehicle"
                      className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-14 text-base"
                    >
                      <SelectValue placeholder="Choose your vehicle" />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map((vehicle) => (
                        <SelectItem key={vehicle._id} value={vehicle._id}>
                          {vehicle.make} {vehicle.model} - {vehicle.license_plate}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="date" className="text-gray-700 font-medium text-lg mb-3 block">
                    Select Date
                  </Label>
                  <Input
                    id="date"
                    data-testid="select-date"
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-14 text-base"
                    min={new Date().toISOString().split("T")[0]}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="timeSlot" className="text-gray-700 font-medium text-lg mb-3 block">
                    Select Time Slot
                  </Label>
                  {timeSlots.length === 0 ? (
                    <p className="text-gray-600">No time slots available. Contact admin.</p>
                  ) : (
                    <Select value={selectedTimeSlot} onValueChange={setSelectedTimeSlot}>
                      <SelectTrigger
                        data-testid="select-timeslot"
                        className="bg-gray-50 border-transparent focus:border-[#1A3C34] rounded-lg h-14 text-base"
                      >
                        <SelectValue placeholder="Choose time slot" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => (
                          <SelectItem key={slot._id} value={slot._id}>
                            {slot.name} ({slot.start_time} - {slot.end_time})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                <Button
                  data-testid="submit-booking"
                  type="submit"
                  disabled={loading || timeSlots.length === 0}
                  className="w-full bg-[#1A3C34] hover:bg-[#142E28] text-white rounded-full py-6 text-lg font-medium transition-transform hover:scale-105 active:scale-95"
                >
                  {loading ? "Booking..." : "Book Parking Slot"}
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;